import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { useSpecProjects } from '../../../hooks/useSpecRequirements.js';
import type { SpecProjectSummary } from '../../../lib/api/spec-requirements.js';

/**
 * レビューの状態の呼び名の翻訳キー。API は保存された値をそのまま返すので、
 * 状態の値（API の識別子）を鍵にして呼び名だけを訳す。
 * @graph-connects none
 */
const REVIEW_STATUS_KEY: Record<string, string> = {
  requested: 'spec.list.reviewStatus.requested',
  ai_changes_requested: 'spec.list.reviewStatus.aiChangesRequested',
  ai_approved: 'spec.list.reviewStatus.aiApproved',
  changes_requested: 'spec.list.reviewStatus.changesRequested',
  approved: 'spec.list.reviewStatus.approved',
};

/** 表の列の翻訳キー。並びはそのまま列の並びになる。 @graph-connects none */
const COLUMN_KEYS = [
  'spec.list.columns.project',
  'spec.list.columns.aiReview',
  'spec.list.columns.dleReview',
  'spec.list.columns.flows',
  'spec.list.columns.narrative',
  'spec.list.columns.review',
  'spec.list.columns.updatedAt',
] as const;

/** 表の日付。時刻までは出さない（一覧で見たいのは新しさの順だけ）。 @graph-connects none */
function day(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * レビュー状態の呼び名。知らない値は API の返した値をそのまま出す（訳せない値を隠さない）。
 * @graph-connects none
 */
function reviewStatusLabel(t: TFunction, status: string): string {
  const key = REVIEW_STATUS_KEY[status];
  return key === undefined ? status : t(key);
}

/** レビュー待ちが 1 件でもあるか。 @graph-connects none */
function isWaiting(project: SpecProjectSummary): boolean {
  return project.aiReviewWaiting > 0 || project.dleReviewWaiting > 0;
}

/**
 * 担当が引けない DLE 待ちを 事業 × チームごとにまとめる（件数の多い順）。
 *
 * **絞り込み前の全件から数える。** 担当が引けない案件は `dleWaitingForMe` が false なので、
 * 「自分宛ての DLE 待ちだけ」を掛けると消える。消したうえで出さないと、引けないことが
 * 誰にも見えないまま滞留する。
 * @graph-connects none
 */
function unassignedGroups(
  projects: readonly SpecProjectSummary[],
): { key: string; business: string; team: string; count: number }[] {
  const groups = new Map<string, { business: string; team: string; count: number }>();
  for (const p of projects) {
    if (!p.dleAssigneeMissing) continue;
    const key = `${p.business}\u0000${p.team}`;
    const found = groups.get(key);
    if (found === undefined) groups.set(key, { business: p.business, team: p.team, count: 1 });
    else found.count += 1;
  }
  return [...groups.entries()]
    .map(([key, g]) => ({ key, ...g }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

/**
 * 成果物のある案件の一覧。
 *
 * **要件定義を開いたら、まず案件を選ぶ。** 以前は先頭の案件の詳細をいきなり出していたが、
 * 開いた人が頼んでいない案件が表示されるので、一覧から入る形にした。詳細は案件ごとのページ。
 *
 * **レビュー待ちは列で出す。** 以前は「AI レビュー待ち」「DLE レビュー待ち」を成果物 1 件
 * 1 行の別の節として案件の表の前に並べていた。同じ案件が節と表に何度も出てきて、案件を
 * 選ぶという本来の用途の邪魔になっていた。待っている数は案件ごとに数えれば足りる
 * （どの成果物が待っているかは案件を開けば分かる）。
 *
 * **既定は「レビュー待ちがあるものだけ」。** 成果物のある案件は増え続けるので、全部出すと
 * 今動かすべきものが埋もれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義の案件を一覧で出し、レビュー待ちの件数を列で見せて案件ごとの詳細ページへ送る
 * @graph-connects /spec-requirements [calls] 案件一覧の取得
 */
export function SpecProjectList(): ReactNode {
  const { t } = useTranslation();
  const list = useSpecProjects();
  const projects = list.data?.projects ?? [];
  const unassigned = unassignedGroups(projects);

  const [waitingOnly, setWaitingOnly] = useState(true);
  const [createdOnly, setCreatedOnly] = useState(false);
  const [myDleOnly, setMyDleOnly] = useState(false);
  const [includeSub, setIncludeSub] = useState(true);

  const visible = projects.filter((p) => {
    if (waitingOnly && !isWaiting(p)) return false;
    if (createdOnly && !p.createdByMe) return false;
    if (myDleOnly && !(p.dleWaitingForMe || (includeSub && p.dleWaitingForMeAsSub))) return false;
    return true;
  });

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold">{t('spec.list.title')}</h1>
      <p className="text-sm text-[var(--color-text-muted)] mt-1">{t('spec.list.description')}</p>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
          <Checkbox
            checked={waitingOnly}
            onChange={(e) => {
              setWaitingOnly(e.target.checked);
            }}
          />
          {t('spec.list.filters.waitingOnly')}
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
          <Checkbox
            checked={createdOnly}
            onChange={(e) => {
              setCreatedOnly(e.target.checked);
            }}
          />
          {t('spec.list.filters.createdOnly')}
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
          <Checkbox
            checked={myDleOnly}
            onChange={(e) => {
              setMyDleOnly(e.target.checked);
            }}
          />
          {t('spec.list.filters.myDleOnly')}
        </label>
        {myDleOnly && (
          <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
            <Checkbox
              checked={includeSub}
              onChange={(e) => {
                setIncludeSub(e.target.checked);
              }}
            />
            {t('spec.list.filters.includeSub')}
          </label>
        )}
      </div>

      {/* **絞り込みの外に出す。** 担当が引けない案件は「自分宛て」に出ないので、
          絞り込みの中に置くと見えない */}
      {unassigned.length > 0 && (
        <section className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
          <h3 className="text-sm font-semibold">{t('spec.list.unassigned.heading')}</h3>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('spec.list.unassigned.description')}
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {unassigned.map((g) => (
              <li key={g.key}>
                {g.team === ''
                  ? t('spec.list.unassigned.noTeam', { count: g.count })
                  : t('spec.list.unassigned.row', {
                      business: g.business,
                      team: g.team,
                      count: g.count,
                    })}
              </li>
            ))}
          </ul>
          <Link
            to="/team-assignments"
            className="mt-2 inline-block text-sm text-[var(--color-accent-primary)] hover:underline"
          >
            {t('spec.list.unassigned.link')}
          </Link>
        </section>
      )}

      {list.error !== null ? (
        <p className="mt-6 text-sm text-[var(--color-text-secondary)]">
          {t('spec.list.loadFailed')}
        </p>
      ) : list.data === undefined ? (
        <p className="mt-6 text-sm text-[var(--color-text-muted)]">{t('spec.loading')}</p>
      ) : projects.length === 0 ? (
        <p className="mt-6 text-sm text-[var(--color-text-muted)]">{t('spec.list.empty')}</p>
      ) : visible.length === 0 ? (
        <p className="mt-6 text-sm text-[var(--color-text-muted)]">{t('spec.list.emptyWaiting')}</p>
      ) : (
        <div className="mt-6 overflow-x-auto border border-[var(--color-border)] rounded-lg">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[var(--color-bg-hover)]">
                {COLUMN_KEYS.map((key) => (
                  <th
                    key={key}
                    className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap"
                  >
                    {t(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((p, index) => (
                <tr
                  key={p.pjKey}
                  className={
                    index % 2 === 1
                      ? 'bg-[var(--color-bg-elevated)]'
                      : 'bg-[var(--color-bg-surface)]'
                  }
                >
                  <td className="px-3 py-2 border-t border-[var(--color-border)]">
                    <Link
                      to="/projects/$pjKey/spec"
                      params={{ pjKey: p.pjKey }}
                      className="text-[var(--color-accent-primary)] hover:underline"
                    >
                      {p.pjTitle === '' ? p.pjKey : p.pjTitle}
                    </Link>
                    <span className="block text-xs text-[var(--color-text-muted)]">{p.pjKey}</span>
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                    {p.aiReviewWaiting === 0
                      ? '-'
                      : t('spec.list.waitingCount', { n: p.aiReviewWaiting })}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                    {p.dleReviewWaiting === 0
                      ? '-'
                      : t('spec.list.waitingCount', { n: p.dleReviewWaiting })}
                    {/* 自分宛てかは一覧で分かるようにする。絞り込みを掛けないと気付けないのでは遅い */}
                    {p.dleWaitingForMe && (
                      <span className="ml-1 text-xs text-[var(--color-accent-primary)]">
                        {t('spec.list.forMe')}
                      </span>
                    )}
                    {!p.dleWaitingForMe && p.dleWaitingForMeAsSub && (
                      <span className="ml-1 text-xs text-[var(--color-text-muted)]">
                        {t('spec.list.forMeAsSub')}
                      </span>
                    )}
                    {/* 引けないことを行でも言う。上の節だけだとどの案件か分からない */}
                    {p.dleAssigneeMissing && (
                      <span className="ml-1 text-xs text-[var(--color-text-secondary)]">
                        {t('spec.list.unassigned.mark')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                    {t('spec.list.flowCount', { n: p.flowCount })}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                    {t(p.hasNarrative ? 'spec.list.narrativeYes' : 'spec.list.narrativeNo')}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                    {p.status === ''
                      ? t('spec.list.reviewNotRequested')
                      : reviewStatusLabel(t, p.status)}
                  </td>
                  <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap text-[var(--color-text-secondary)]">
                    {day(p.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
